import { Fragment, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Button } from '../../components/UI';
import { TKLogo } from '../../components/Logo';
import { useAuth, useAuthConfig } from '../../lib/auth';
import { ApiClientError } from '../../lib/api';

export type AuthMode = 'signin' | 'signup';

interface AuthProps {
  mode?: AuthMode;
  onComplete: () => void;
}

export const Auth = ({ mode: initialMode = 'signin', onComplete }: AuthProps) => {
  const [requestedMode, setMode] = useState<AuthMode>(initialMode);
  const { signupEnabled } = useAuthConfig();
  // Closed deployments only ever show sign-in, whatever the URL asked for.
  const mode: AuthMode = signupEnabled ? requestedMode : 'signin';
  const [narrow, setNarrow] = useState(() => window.innerWidth < 980);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 980);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '1fr 1.05fr',
        background: 'var(--tk-bg)',
      }}
    >
      <AuthFormSide mode={mode} setMode={setMode} onComplete={onComplete} narrow={narrow} signupEnabled={signupEnabled} />
      {!narrow && <AuthBrandSide />}
    </div>
  );
};

interface FormSideProps {
  mode: AuthMode;
  setMode: (m: AuthMode) => void;
  onComplete: () => void;
  narrow: boolean;
  signupEnabled: boolean;
}

const AuthFormSide = ({ mode, setMode, onComplete, narrow, signupEnabled }: FormSideProps) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      padding: narrow ? '24px 20px 32px' : '40px 56px 40px',
      background: 'var(--tk-bg)',
      minHeight: narrow ? '100vh' : 'auto',
    }}
  >
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 32,
      }}
    >
      <TKLogo size={28} />
      <div style={{ fontSize: 13, color: 'var(--tk-ink-3)' }}>
        {mode === 'signup' ? (
          <>
            Already have an account?{' '}
            <ModeLink onClick={() => setMode('signin')}>Sign in</ModeLink>
          </>
        ) : signupEnabled ? (
          <>
            New to TradeKaro?{' '}
            <ModeLink onClick={() => setMode('signup')}>Create account</ModeLink>
          </>
        ) : (
          <>Invite only · sign-ups are closed</>
        )}
      </div>
    </div>

    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {mode === 'signup' ? (
          <SignupFlow onComplete={onComplete} />
        ) : (
          <SigninFlow onComplete={onComplete} />
        )}
      </div>
    </div>

    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11.5,
        color: 'var(--tk-ink-3)',
        marginTop: 32,
      }}
    >
      <span>© 2026 TradeKaro · SECP Regulated</span>
      <div style={{ display: 'flex', gap: 18 }}>
        <a href="#" style={{ color: 'inherit' }}>
          Privacy
        </a>
        <a href="#" style={{ color: 'inherit' }}>
          Terms
        </a>
        <a href="#" style={{ color: 'inherit' }}>
          Help
        </a>
      </div>
    </div>
  </div>
);

const ModeLink = ({ children, onClick }: { children: ReactNode; onClick: () => void }) => (
  <button
    onClick={onClick}
    style={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: 'var(--tk-accent)',
      fontSize: 13,
      fontWeight: 700,
      fontFamily: 'var(--tk-sans)',
      padding: 0,
    }}
  >
    {children}
  </button>
);

const SigninFlow = ({ onComplete }: { onComplete: () => void }) => {
  const [method, setMethod] = useState<'phone' | 'email'>('email');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { signin } = useAuth();

  const onSubmit = async () => {
    setError(null);
    if (method === 'phone') {
      setError('Phone OTP not wired yet — use email + password');
      return;
    }
    if (!email || !pwd) {
      setError('Email and password required');
      return;
    }
    setSubmitting(true);
    try {
      await signin(email, pwd);
      onComplete();
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message);
      else setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tk-fade-in">
      <Eyebrow>Welcome back</Eyebrow>
      <h1
        style={{
          margin: '6px 0 8px',
          fontSize: 34,
          fontWeight: 700,
          letterSpacing: '-0.028em',
          lineHeight: 1.1,
        }}
      >
        Sign in to <span style={{ color: 'var(--tk-accent)' }}>TradeKaro</span>
      </h1>
      <p
        style={{ margin: '0 0 32px', color: 'var(--tk-ink-2)', fontSize: 14, lineHeight: 1.5 }}
      >
        Pick up where you left off. Markets are{' '}
        <strong style={{ color: 'var(--tk-up)' }}>open</strong> and KSE-100 is up{' '}
        <strong className="mono">+0.84%</strong> today.
      </p>

      <SegToggle
        options={[
          { id: 'phone', label: 'Phone' },
          { id: 'email', label: 'Email' },
        ]}
        value={method}
        onChange={(v) => setMethod(v as 'phone' | 'email')}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitting) void onSubmit();
        }}
        style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {method === 'phone' ? (
          <Field label="Mobile number">
            <PhoneInput value={phone} onChange={setPhone} />
          </Field>
        ) : (
          <Field label="Email address">
            <TextInput
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="ali@example.com"
              autoFocus
              autoComplete="email"
              name="email"
            />
          </Field>
        )}

        {method === 'email' && (
          <Field
            label="Password"
            hint={
              <a
                href="#"
                tabIndex={-1}
                style={{ color: 'var(--tk-accent)', fontSize: 12, fontWeight: 600 }}
              >
                Forgot?
              </a>
            }
          >
            <TextInput
              type={showPwd ? 'text' : 'password'}
              value={pwd}
              onChange={setPwd}
              placeholder="••••••••"
              autoComplete="current-password"
              name="password"
              suffix={
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd((s) => !s)}
                  style={iconBtn}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {showPwd ? (
                      <>
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </>
                    ) : (
                      <>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <path d="M2 2l20 20" />
                      </>
                    )}
                  </svg>
                </button>
              }
            />
          </Field>
        )}

        {error && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--tk-radius-sm)',
              background: 'var(--tk-down-soft)',
              color: 'var(--tk-down)',
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          style={{
            width: '100%',
            justifyContent: 'center',
            marginTop: 8,
            opacity: submitting ? 0.6 : 1,
            pointerEvents: submitting ? 'none' : 'auto',
          }}
          onClick={onSubmit}
        >
          {submitting ? 'Signing in…' : method === 'phone' ? 'Continue with OTP' : 'Sign in'}
        </Button>

        <Divider>or continue with</Divider>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <SocialBtn provider="google" />
          <SocialBtn provider="apple" />
        </div>
      </form>

      <TrustStrip style={{ marginTop: 32 }} />
    </div>
  );
};

const SIGNUP_STEPS = [
  { id: 'account', label: 'Account' },
  { id: 'identity', label: 'Identity' },
  { id: 'verify', label: 'Verify' },
];

interface SignupData {
  phone: string;
  email: string;
  name: string;
  cnic: string;
  password: string;
  otp: string[];
}

const SignupFlow = ({ onComplete }: { onComplete: () => void }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<SignupData>({
    phone: '',
    email: '',
    name: '',
    cnic: '',
    password: '',
    otp: ['', '', '', '', '', ''],
  });

  const update = <K extends keyof SignupData>(k: K, v: SignupData[K]) =>
    setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="tk-fade-in">
      <Stepper steps={SIGNUP_STEPS} current={step} />

      <div style={{ marginTop: 28 }}>
        {step === 0 && <SignupStepAccount data={data} update={update} onNext={() => setStep(1)} />}
        {step === 1 && (
          <SignupStepIdentity
            data={data}
            update={update}
            onNext={() => setStep(2)}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && (
          <SignupStepVerify
            data={data}
            onComplete={onComplete}
            onBack={() => setStep(1)}
          />
        )}
      </div>
    </div>
  );
};

const Stepper = ({
  steps,
  current,
}: {
  steps: { id: string; label: string }[];
  current: number;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
    {steps.map((s, i) => {
      const state = i < current ? 'done' : i === current ? 'active' : 'pending';
      return (
        <Fragment key={s.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background:
                  state === 'done'
                    ? 'var(--tk-accent)'
                    : state === 'active'
                      ? 'var(--tk-ink)'
                      : 'var(--tk-surface-2)',
                color: state === 'pending' ? 'var(--tk-ink-3)' : 'var(--tk-bg)',
                fontSize: 11,
                fontWeight: 700,
                fontFamily: 'var(--tk-mono)',
                border: state === 'pending' ? '1px solid var(--tk-line)' : 'none',
                transition: 'all 0.25s var(--tk-ease)',
              }}
            >
              {state === 'done' ? (
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
              ) : (
                i + 1
              )}
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: state === 'active' ? 700 : 600,
                color: state === 'pending' ? 'var(--tk-ink-3)' : 'var(--tk-ink)',
                letterSpacing: '-0.005em',
              }}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                margin: '0 12px',
                borderRadius: 1,
                background: i < current ? 'var(--tk-accent)' : 'var(--tk-line)',
                transition: 'background 0.3s var(--tk-ease)',
              }}
            />
          )}
        </Fragment>
      );
    })}
  </div>
);

interface SignupStepProps {
  data: SignupData;
  update: <K extends keyof SignupData>(k: K, v: SignupData[K]) => void;
  onNext?: () => void;
  onBack?: () => void;
}

const SignupStepAccount = ({ data, update, onNext }: SignupStepProps) => (
  <div>
    <Eyebrow>Step 1 of 3</Eyebrow>
    <h1
      style={{
        margin: '6px 0 8px',
        fontSize: 30,
        fontWeight: 700,
        letterSpacing: '-0.028em',
        lineHeight: 1.15,
      }}
    >
      Open your investing account
    </h1>
    <p style={{ margin: '0 0 28px', color: 'var(--tk-ink-2)', fontSize: 14, lineHeight: 1.5 }}>
      Takes about 4 minutes. You&apos;ll need your CNIC handy.
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Full name (as on CNIC)">
        <TextInput
          value={data.name}
          onChange={(v) => update('name', v)}
          placeholder="Ali Raza"
        />
      </Field>
      <Field
        label="Mobile number"
        hint={
          <span style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>We&apos;ll send an OTP</span>
        }
      >
        <PhoneInput value={data.phone} onChange={(v) => update('phone', v)} />
      </Field>
      <Field label="Email address">
        <TextInput
          type="email"
          value={data.email}
          onChange={(v) => update('email', v)}
          placeholder="ali@example.com"
        />
      </Field>
      <Field label="Password" hint={<span style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>8+ characters</span>}>
        <TextInput
          type="password"
          value={data.password}
          onChange={(v) => update('password', v)}
          placeholder="••••••••"
        />
      </Field>

      <Checkbox
        label={
          <>
            I agree to TradeKaro&apos;s{' '}
            <a href="#" style={{ color: 'var(--tk-accent)' }}>
              Terms
            </a>{' '}
            and{' '}
            <a href="#" style={{ color: 'var(--tk-accent)' }}>
              Privacy Policy
            </a>
            , and consent to NCCPL/CDC sharing for trading activity.
          </>
        }
      />

      <Button
        variant="primary"
        size="lg"
        style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
        onClick={onNext}
      >
        Continue
      </Button>

      <Divider>or sign up with</Divider>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <SocialBtn provider="google" />
        <SocialBtn provider="apple" />
      </div>
    </div>
  </div>
);

const SignupStepIdentity = ({ data, update, onNext, onBack }: SignupStepProps) => (
  <div>
    <Eyebrow>Step 2 of 3 · Identity</Eyebrow>
    <h1
      style={{
        margin: '6px 0 8px',
        fontSize: 30,
        fontWeight: 700,
        letterSpacing: '-0.028em',
        lineHeight: 1.15,
      }}
    >
      Verify your CNIC
    </h1>
    <p style={{ margin: '0 0 28px', color: 'var(--tk-ink-2)', fontSize: 14, lineHeight: 1.5 }}>
      We&apos;re regulated by SECP — verifying your CNIC is required to trade Pakistani securities.
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="CNIC number">
        <CNICInput value={data.cnic} onChange={(v) => update('cnic', v)} />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <UploadTile label="CNIC front" />
        <UploadTile label="CNIC back" />
      </div>

      <SecurityNote>
        Your CNIC images are encrypted end-to-end and verified via NADRA in under 60 seconds. We
        never store your scan after verification.
      </SecurityNote>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <Button
          variant="ghost"
          size="lg"
          onClick={onBack}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          Back
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          style={{ flex: 2, justifyContent: 'center' }}
        >
          Verify with NADRA
        </Button>
      </div>
    </div>
  </div>
);

const SignupStepVerify = ({
  data,
  onComplete,
  onBack,
}: {
  data: SignupData;
  onComplete: () => void;
  onBack: () => void;
}) => {
  const [otp, setOtp] = useState<string[]>(data.otp);
  const [seconds, setSeconds] = useState(38);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const { signup } = useAuth();

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const setDigit = (i: number, v: string) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const filled = otp.every((d) => d !== '');

  const onSubmit = async () => {
    setError(null);
    if (!filled) return;
    if (data.password.length < 8) {
      setError('Password must be at least 8 characters (set in step 1)');
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        name: data.name,
        email: data.email,
        phone: data.phone || undefined,
        cnic: data.cnic || undefined,
        password: data.password,
      });
      onComplete();
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message);
      else setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Eyebrow>Step 3 of 3 · Verification</Eyebrow>
      <h1
        style={{
          margin: '6px 0 8px',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '-0.028em',
          lineHeight: 1.15,
        }}
      >
        Enter the 6-digit code
      </h1>
      <p style={{ margin: '0 0 28px', color: 'var(--tk-ink-2)', fontSize: 14, lineHeight: 1.5 }}>
        Sent to <strong style={{ color: 'var(--tk-ink)' }}>{data.phone || '+92 3XX XXXXXXX'}</strong>{' '}
        <button
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--tk-accent)',
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 'inherit',
          }}
        >
          Edit
        </button>
      </p>

      <div
        style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginBottom: 18 }}
      >
        {otp.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKey(i, e)}
            inputMode="numeric"
            maxLength={1}
            style={{
              width: 54,
              height: 64,
              fontSize: 26,
              fontWeight: 700,
              fontFamily: 'var(--tk-mono)',
              textAlign: 'center',
              color: 'var(--tk-ink)',
              background: 'var(--tk-surface)',
              border: d ? '2px solid var(--tk-accent)' : '1px solid var(--tk-line)',
              borderRadius: 'var(--tk-radius-md)',
              outline: 'none',
              transition: 'all 0.15s var(--tk-ease)',
            }}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>

      <div
        style={{
          fontSize: 12.5,
          color: 'var(--tk-ink-3)',
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>
          Didn&apos;t receive?{' '}
          <button
            disabled={seconds > 0}
            onClick={() => setSeconds(38)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: seconds > 0 ? 'default' : 'pointer',
              color: seconds > 0 ? 'var(--tk-ink-3)' : 'var(--tk-accent)',
              fontWeight: 600,
              padding: 0,
              fontFamily: 'inherit',
              fontSize: 'inherit',
            }}
          >
            Resend
          </button>
        </span>
        {seconds > 0 && (
          <span className="mono" style={{ color: 'var(--tk-ink-3)' }}>
            in {String(Math.floor(seconds / 60)).padStart(2, '0')}:
            {String(seconds % 60).padStart(2, '0')}
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--tk-radius-sm)',
            background: 'var(--tk-down-soft)',
            color: 'var(--tk-down)',
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <Button
          variant="ghost"
          size="lg"
          onClick={onBack}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          Back
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onSubmit}
          style={{
            flex: 2,
            justifyContent: 'center',
            opacity: filled && !submitting ? 1 : 0.55,
            pointerEvents: filled && !submitting ? 'auto' : 'none',
          }}
        >
          {submitting ? 'Creating…' : 'Create my account'}
        </Button>
      </div>

      <div
        style={{
          marginTop: 24,
          padding: '14px 16px',
          background: 'var(--tk-up-soft)',
          borderRadius: 'var(--tk-radius-md)',
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            flexShrink: 0,
            background: 'var(--tk-up)',
            color: 'var(--tk-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
        </span>
        <div style={{ fontSize: 12.5, color: 'var(--tk-ink-2)' }}>
          <strong style={{ color: 'var(--tk-ink)' }}>NADRA verified.</strong> Your CNIC matches and
          your name is confirmed. You&apos;re almost in.
        </div>
      </div>
    </div>
  );
};

const AuthBrandSide = () => {
  return (
    <div
      style={{
        position: 'relative',
        background:
          'linear-gradient(155deg, oklch(0.5 0.13 38) 0%, oklch(0.42 0.12 32) 60%, oklch(0.32 0.08 25) 100%)',
        color: 'oklch(0.99 0.005 60)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 56px',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(900px 600px at 20% 10%, oklch(0.7 0.2 50 / 0.35), transparent 60%),
                       radial-gradient(700px 500px at 90% 90%, oklch(0.6 0.15 25 / 0.3), transparent 55%)`,
          pointerEvents: 'none',
        }}
      />
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06 }}>
        <defs>
          <pattern id="dotgrid" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dotgrid)" />
      </svg>

      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'color-mix(in oklch, white 14%, transparent)',
              border: '1px solid color-mix(in oklch, white 22%, transparent)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'var(--tk-mono)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: 'oklch(0.85 0.18 150)',
                animation: 'tk-pulse 1.6s var(--tk-ease) infinite',
              }}
            />
            PSX Live
          </span>
          <span style={{ fontSize: 11.5, opacity: 0.7, fontFamily: 'var(--tk-mono)' }}>
            Karachi · {new Date().toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}{' '}
            PKT
          </span>
        </div>

        <div style={{ marginTop: 64, maxWidth: 480 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 44,
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
            }}
          >
            Pakistan&apos;s market,
            <br />
            <span
              style={{
                background: 'linear-gradient(90deg, oklch(0.9 0.13 50), oklch(0.85 0.18 28))',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              built for everyone.
            </span>
          </h2>
          <p
            style={{
              marginTop: 18,
              fontSize: 15.5,
              lineHeight: 1.55,
              opacity: 0.78,
              maxWidth: 440,
            }}
          >
            Buy and sell shares listed on the Pakistan Stock Exchange, track your portfolio in
            real-time, and move money between your bank and wallet — all in minutes.
          </p>
        </div>

        <div style={{ marginTop: 40 }}>
          <LivePsxTicker />
        </div>

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 40,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
            borderTop: '1px solid color-mix(in oklch, white 18%, transparent)',
          }}
        >
          <BrandStat value="120K+" label="Active investors" />
          <BrandStat value="₨ 8.4B" label="Volume traded YTD" />
          <BrandStat value="4.8★" label="App store rating" />
        </div>

        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 11.5,
              opacity: 0.7,
              fontFamily: 'var(--tk-mono)',
              letterSpacing: '0.04em',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <span>SECP regulated · Funds segregated at MCB & Meezan · 256-bit encryption</span>
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', opacity: 0.75 }}>
            {['MCB', 'Meezan', 'HBL', 'UBL', 'BAFL'].map((b) => (
              <span
                key={b}
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  fontFamily: 'var(--tk-mono)',
                  padding: '5px 9px',
                  borderRadius: 4,
                  background: 'color-mix(in oklch, white 10%, transparent)',
                  border: '1px solid color-mix(in oklch, white 16%, transparent)',
                }}
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const BrandStat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <div
      className="mono"
      style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}
    >
      {value}
    </div>
    <div
      style={{ fontSize: 11.5, opacity: 0.65, marginTop: 6, letterSpacing: '0.02em' }}
    >
      {label}
    </div>
  </div>
);

const LivePsxTicker = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(id);
  }, []);

  const stocks = [
    { sym: 'KSE100', price: 79284.15, change: 0.84 },
    { sym: 'ENGROH', price: 322.85, change: 1.42 },
    { sym: 'MEBL', price: 226.2, change: 0.65 },
    { sym: 'OGDC', price: 142.1, change: -0.32 },
  ];

  return (
    <div
      style={{
        background: 'color-mix(in oklch, white 8%, transparent)',
        border: '1px solid color-mix(in oklch, white 16%, transparent)',
        borderRadius: 'var(--tk-radius-lg)',
        padding: '18px 20px',
        backdropFilter: 'blur(12px)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontFamily: 'var(--tk-mono)',
            opacity: 0.7,
          }}
        >
          Today on PSX
        </span>
        <span style={{ fontSize: 11, opacity: 0.55, fontFamily: 'var(--tk-mono)' }}>↑ live</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {stocks.map((s, i) => {
          const wiggle = Math.sin(tick * 0.7 + i) * 0.05;
          const livePrice = s.price * (1 + wiggle / 100);
          return (
            <div
              key={s.sym}
              style={{
                display: 'grid',
                gridTemplateColumns: '70px 1fr 90px',
                alignItems: 'center',
                gap: 14,
                fontFamily: 'var(--tk-mono)',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.01em' }}>
                {s.sym}
              </span>
              <MiniPulse up={s.change >= 0} seed={i} tick={tick} />
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {livePrice.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: s.change >= 0 ? 'oklch(0.85 0.18 150)' : 'oklch(0.85 0.18 28)',
                  }}
                >
                  {s.change >= 0 ? '+' : ''}
                  {s.change.toFixed(2)}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MiniPulse = ({ up, seed, tick }: { up: boolean; seed: number; tick: number }) => {
  const W = 120;
  const H = 28;
  const N = 40;
  const points = Array.from({ length: N }, (_, i) => {
    const x = (i / (N - 1)) * W;
    const phase = seed * 1.3 + tick * 0.4;
    const y =
      H / 2 +
      Math.sin(i * 0.5 + phase) * 7 +
      Math.sin(i * 1.7 + phase * 0.7) * 4 -
      (up ? i * 0.18 : -i * 0.18) +
      (up ? 6 : -6);
    return [x, Math.max(2, Math.min(H - 2, y))] as [number, number];
  });
  const d = points.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const stroke = up ? 'oklch(0.85 0.18 150)' : 'oklch(0.85 0.18 28)';
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
    </svg>
  );
};

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'var(--tk-accent)',
      fontFamily: 'var(--tk-mono)',
    }}
  >
    {children}
  </span>
);

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <label style={{ display: 'block' }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 7,
      }}
    >
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: 'var(--tk-ink-2)',
          letterSpacing: '-0.005em',
        }}
      >
        {label}
      </span>
      {hint}
    </div>
    {children}
  </label>
);

const inputBase: CSSProperties = {
  width: '100%',
  padding: '13px 14px',
  fontSize: 14,
  fontFamily: 'var(--tk-sans)',
  background: 'var(--tk-surface)',
  border: '1px solid var(--tk-line)',
  borderRadius: 'var(--tk-radius-sm)',
  color: 'var(--tk-ink)',
  outline: 'none',
  transition: 'all 0.15s var(--tk-ease)',
};

const TextInput = ({
  value,
  onChange,
  type = 'text',
  placeholder,
  suffix,
  autoFocus,
  autoComplete,
  name,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  suffix?: ReactNode;
  autoFocus?: boolean;
  autoComplete?: string;
  name?: string;
}) => {
  const [focus, setFocus] = useState(false);
  return (
    <div
      style={{
        position: 'relative',
        ...inputBase,
        padding: 0,
        borderColor: focus ? 'var(--tk-accent)' : 'var(--tk-line)',
        boxShadow: focus ? '0 0 0 3px var(--tk-accent-soft)' : 'none',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        name={name}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          flex: 1,
          padding: '13px 14px',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 14,
          fontFamily: 'var(--tk-sans)',
          color: 'var(--tk-ink)',
        }}
      />
      {suffix && <div style={{ paddingRight: 10 }}>{suffix}</div>}
    </div>
  );
};

const PhoneInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const [focus, setFocus] = useState(false);
  return (
    <div
      style={{
        ...inputBase,
        padding: 0,
        borderColor: focus ? 'var(--tk-accent)' : 'var(--tk-line)',
        boxShadow: focus ? '0 0 0 3px var(--tk-accent-soft)' : 'none',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '13px 12px 13px 14px',
          borderRight: '1px solid var(--tk-line)',
        }}
      >
        <PkFlag />
        <span className="mono" style={{ fontSize: 13.5, fontWeight: 700 }}>
          +92
        </span>
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d ]/g, ''))}
        placeholder="3XX XXXXXXX"
        inputMode="numeric"
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          flex: 1,
          padding: '13px 14px',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 14,
          fontFamily: 'var(--tk-mono)',
          letterSpacing: '0.02em',
          color: 'var(--tk-ink)',
        }}
      />
    </div>
  );
};

const CNICInput = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const fmt = (raw: string) => {
    const d = raw.replace(/\D/g, '').slice(0, 13);
    if (d.length <= 5) return d;
    if (d.length <= 12) return d.slice(0, 5) + '-' + d.slice(5);
    return d.slice(0, 5) + '-' + d.slice(5, 12) + '-' + d.slice(12);
  };
  return (
    <TextInput value={value} onChange={(v) => onChange(fmt(v))} placeholder="XXXXX-XXXXXXX-X" />
  );
};

const Checkbox = ({ label }: { label: ReactNode }) => {
  const [checked, setChecked] = useState(false);
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        fontSize: 12.5,
        color: 'var(--tk-ink-2)',
        cursor: 'pointer',
        lineHeight: 1.5,
      }}
    >
      <button
        onClick={() => setChecked((v) => !v)}
        type="button"
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          flexShrink: 0,
          marginTop: 1,
          background: checked ? 'var(--tk-accent)' : 'transparent',
          border: checked ? '1px solid var(--tk-accent)' : '1.5px solid var(--tk-line)',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s',
        }}
      >
        {checked && (
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3.5"
            strokeLinecap="round"
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
        )}
      </button>
      <span>{label}</span>
    </label>
  );
};

const SegToggle = ({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) => (
  <div
    style={{
      display: 'flex',
      gap: 4,
      padding: 4,
      background: 'var(--tk-surface-2)',
      borderRadius: 'var(--tk-radius-sm)',
      border: '1px solid var(--tk-line)',
    }}
  >
    {options.map((o) => (
      <button
        key={o.id}
        onClick={() => onChange(o.id)}
        style={{
          flex: 1,
          padding: '9px 10px',
          fontSize: 13,
          fontWeight: 600,
          background: value === o.id ? 'var(--tk-surface)' : 'transparent',
          color: value === o.id ? 'var(--tk-ink)' : 'var(--tk-ink-3)',
          border: 'none',
          borderRadius: 7,
          cursor: 'pointer',
          boxShadow: value === o.id ? 'var(--tk-shadow-sm)' : 'none',
          transition: 'all 0.18s var(--tk-ease)',
        }}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Divider = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      color: 'var(--tk-ink-3)',
      fontSize: 11.5,
      fontFamily: 'var(--tk-mono)',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    }}
  >
    <span style={{ flex: 1, height: 1, background: 'var(--tk-line)' }} />
    {children}
    <span style={{ flex: 1, height: 1, background: 'var(--tk-line)' }} />
  </div>
);

const SocialBtn = ({ provider }: { provider: 'google' | 'apple' }) => {
  const map = {
    google: { label: 'Google', icon: <GoogleIcon /> },
    apple: { label: 'Apple', icon: <AppleIcon /> },
  };
  const m = map[provider];
  return (
    <button
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        padding: '11px 14px',
        background: 'var(--tk-surface)',
        border: '1px solid var(--tk-line)',
        borderRadius: 'var(--tk-radius-sm)',
        color: 'var(--tk-ink)',
        fontSize: 13.5,
        fontWeight: 600,
        fontFamily: 'var(--tk-sans)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--tk-ink-3)';
        e.currentTarget.style.background = 'var(--tk-surface-2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--tk-line)';
        e.currentTarget.style.background = 'var(--tk-surface)';
      }}
    >
      {m.icon}
      {m.label}
    </button>
  );
};

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.6 2.3 30.1 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.3 13 17.6 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.9 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.9c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 6.7-9.9 6.7-17.4z"
    />
    <path
      fill="#FBBC05"
      d="M10.4 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.8 16.5 0 20.1 0 24s.8 7.5 2.5 10.7l7.9-6.1z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.1 0 11.2-2 14.9-5.5l-7.4-5.7c-2.1 1.4-4.7 2.2-7.5 2.2-6.4 0-11.7-3.5-13.6-9.4l-7.9 6.1C6.4 42.6 14.6 48 24 48z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.4 12.7c0-2.4 2-3.5 2.1-3.6-1.2-1.7-3-1.9-3.6-2-1.6-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.1 2.5-1.8 3-.5 7.5 1.2 10 .8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.3.8c1.4 0 2.2-1.2 3.1-2.4.9-1.4 1.3-2.7 1.4-2.8-.1 0-2.6-1-2.6-4zM14 5.4c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
  </svg>
);

const PkFlag = () => (
  <svg width="20" height="14" viewBox="0 0 30 20" style={{ borderRadius: 2, overflow: 'hidden' }}>
    <rect width="30" height="20" fill="#01411C" />
    <rect width="7" height="20" fill="white" />
    <circle cx="18" cy="10" r="4.2" fill="#01411C" />
    <circle cx="19.5" cy="9" r="4" fill="white" />
    <polygon
      points="22.6,11.4 23.5,12 23.1,11 23.9,10.4 22.9,10.4 22.6,9.4 22.3,10.4 21.3,10.4 22.1,11"
      fill="white"
    />
  </svg>
);

const iconBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--tk-ink-3)',
  padding: 8,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const UploadTile = ({ label }: { label: string }) => (
  <button
    style={{
      padding: '20px 16px',
      background: 'var(--tk-surface-2)',
      border: '1.5px dashed var(--tk-line)',
      borderRadius: 'var(--tk-radius-md)',
      color: 'var(--tk-ink-2)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = 'var(--tk-accent)';
      e.currentTarget.style.color = 'var(--tk-accent)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = 'var(--tk-line)';
      e.currentTarget.style.color = 'var(--tk-ink-2)';
    }}
  >
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="m21 16-4.5-4.5L9 19" />
    </svg>
    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
    <span
      style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', fontFamily: 'var(--tk-mono)' }}
    >
      JPG / PDF · ≤ 5MB
    </span>
  </button>
);

const SecurityNote = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      padding: '12px 14px',
      background: 'var(--tk-surface-2)',
      borderRadius: 'var(--tk-radius-sm)',
      fontSize: 12,
      color: 'var(--tk-ink-2)',
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      lineHeight: 1.45,
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--tk-accent)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, marginTop: 1 }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
    <span>{children}</span>
  </div>
);

const TrustStrip = ({ style }: { style?: CSSProperties }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 22,
      borderTop: '1px solid var(--tk-line-2)',
      fontSize: 11,
      color: 'var(--tk-ink-3)',
      fontFamily: 'var(--tk-mono)',
      letterSpacing: '0.04em',
      ...style,
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      256-bit encrypted
    </span>
    <span>SECP regulated</span>
    <span>120K+ investors</span>
  </div>
);
